/**
 * Client-safe backup-credential schemas (CLA-555).
 *
 * The error types and wire schema for the backup-credential mint path live
 * here, split out from `backup-creds.ts`, because that module imports the
 * Node-only `@aws-sdk/client-sts` at the top level. Client bundles (the RN
 * app via `@fm/api/rpc`) import these schemas transitively through
 * `rpc/errors.ts` and `rpc/mgmt.ts`; keeping them in an AWS-SDK-free module
 * stops Metro from trying to bundle `node:https` into the app.
 *
 * `backup-creds.ts` re-exports every symbol here, so server consumers that
 * import from `@fm/cluster/services/backup-creds` are unchanged.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";

/**
 * Raised by the backup-credential mint path when the backup storage
 * environment is not configured — `S3_BACKUP_BUCKET` and/or
 * `BACKUP_ROLE_ARN` are unset. The clawd backup fiber treats this as a
 * silent skip so the feature ships dark until ops sets the env.
 *
 * @since 1.0.0
 */
export class BackupStorageNotConfigured extends Schema.TaggedError<BackupStorageNotConfigured>()(
  "BackupStorageNotConfigured",
  {
    message: Schema.String,
  },
) {}

/**
 * Raised by the backup-credential mint path when the `sts:AssumeRole`
 * call against `BACKUP_ROLE_ARN` fails (e.g. the api still authenticates
 * as the AWS account root, which cannot assume roles). The clawd backup
 * fiber treats this as a skip + logged warning so the loop keeps ticking
 * until the identity fix lands.
 *
 * @since 1.0.0
 */
export class BackupCredentialMintFailed extends Schema.TaggedError<BackupCredentialMintFailed>()(
  "BackupCredentialMintFailed",
  {
    message: Schema.String,
  },
) {}

/**
 * Wire response for `mgmt.fetchBackupCredentials`. Carries temporary,
 * per-install STS credentials the agent passes to the restic child via
 * env (never written to disk). `endpoint` is composed server-side
 * (`https://s3.<region>.amazonaws.com`) so the wire shape stays
 * provider-agnostic. `expiresAt` is `Schema.DateTimeUtc` per the api
 * timestamp convention.
 *
 * @since 1.0.0
 */
export const FetchBackupCredentialsResponse = Schema.Struct({
  endpoint: Schema.String,
  bucket: Schema.String,
  accessKeyId: Schema.String,
  secretAccessKey: Schema.String,
  sessionToken: Schema.String,
  expiresAt: Schema.DateTimeUtc,
});
export type FetchBackupCredentialsResponse = Schema.Schema.Type<typeof FetchBackupCredentialsResponse>;
